import { Component, Input, ChangeDetectorRef, OnDestroy } from '@angular/core';
import * as _ from 'lodash';

import { Observable } from 'rxjs';

import { Option } from 'shared/form/select/option.interface';

import { InterchangeService } from 'shared/interchange/interchange.service';
import { CustomFieldsService } from 'shared/custom-fields/custom-fields.service';
import { BrandService } from 'shared/brand/brand.service';
import { PriceSheetService } from 'shared/price-sheet/price-sheet.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';
import { takeWhile } from 'rxjs/operators';
import { Product } from '../../../my-products.interface';

interface CustomFieldOption extends Option {
  type_id?: number | undefined;
}

@Component({
  moduleId: module.id,
  selector: 'advanced-export-options',
  templateUrl: 'advanced-export-options.component.html',
  styleUrls: ['advanced-export-options.component.scss'],
})
export class AdvancedExportOptionsComponent implements OnDestroy {
  @Input() status: string;
  @Input() products: Array<Product> = [];

  customFieldTypesList: Array<Option> = [
    {
      option: 1,
      title: 'Attributes',
    },
    {
      option: 2,
      title: 'Pricing',
    },
    {
      option: 3,
      title: 'Description 80',
    },
    {
      option: 4,
      title: 'Description 200',
    },
    {
      option: 5,
      title: 'Description 240',
    },
    {
      option: 6,
      title: 'Description 2000',
    },
  ];
  customFieldsList: Array<CustomFieldOption> = [];
  availableCustomFieldsList: Array<CustomFieldOption> = [];
  interchangeList: Array<Option> = [];
  fitmentStatusList: Array<Option> = [];
  private isActive: boolean = true;

  constructor(
    protected customFieldsService: CustomFieldsService,
    protected brandService: BrandService,
    protected priceSheetService: PriceSheetService,
    protected interchangeService: InterchangeService,
    protected fitmentsService: FitmentsService,
    protected ref: ChangeDetectorRef,
  ) {}

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get selectedCustomFields(): Array<object> {
    return _.chain(this.availableCustomFieldsList)
      .filter(['selected', true])
      .map(o => {
        return _.omit(o, ['selected', 'title']);
      })
      .value();
  }

  get selectedInterchange(): Array<string|number> {
    return this.getSelectedOptions(this.interchangeList);
  }

  get selectedFitmentStatus(): Array<string|number> {
    return this.getSelectedOptions(this.fitmentStatusList);
  }

  get allSelectedData(): any {
    return {
      customFields: this.selectedCustomFields,
      interchange: this.selectedInterchange,
      fitmentStatus: this.selectedFitmentStatus,
    };
  }

  get brandId(): any {
    return this.brandService.getBrand();
  }

  get selectedCustomFieldTypes(): Array<string|number> {
    return this.getSelectedOptions(this.customFieldTypesList);
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  /*******************************************
  * COMPONENT PUBLIC
  *******************************************/

  public getLists(): void {
    forkJoin([
      this.getInterchangeList(),
      this.getFitmentStatuses(),
      this.getCustomFields()
    ])
    .pipe(takeWhile(() => this.isActive))
    .subscribe((result: Array<Array<Option | CustomFieldOption>>) => {
      this.interchangeList = result[0];
      this.fitmentStatusList = result[1];
      this.customFieldsList = result[2].map((o: CustomFieldOption) => {
        if (o.type_id) {
          return o;
        } else {
          return {
            ...o,
            type_id: 1,
          };
        }
      });
      this.ref.markForCheck();
    });
  }

  public reset(): void {
    this.customFieldsList = [];
    this.interchangeList = [];
    this.fitmentStatusList = [];
  }

  /*******************************************
  * COMPONENT PROTECTED
  *******************************************/

  protected getCustomFields(): Observable<Array<Option>> {
    const itemIds: Array<number> = this.products.map(p => p.itemId);
    return this.customFieldsService.postCustomFields(this.brandId, this.status, itemIds);
  }

  protected getInterchangeList(): Observable<Array<Option>> {
    return this.interchangeService.getInterchangeList(this.brandId, this.status);
  }

  protected getFitmentStatuses(): Observable<Array<Option>> {
    return this.fitmentsService.getFitmentStatuses(this.brandId, this.status);
  }

  protected getSelectedOptions(formatsList: Array<Option>): Array<number|string> {
    return _.chain(formatsList)
    .filter(['selected', true])
    .map('option')
    .value();
  }

  onCustomTypeOptionSelected() {
    this.availableCustomFieldsList = this.customFieldsList.filter(f => this.selectedCustomFieldTypes.includes(f.type_id));
  }

  onCustomTypeAllOptionsSelected() {
    this.availableCustomFieldsList = this.customFieldsList.filter(f => this.selectedCustomFieldTypes.includes(f.type_id));
  }
}
